import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/auth/AuthContext';
import { isWithinFacilityRange, formatDistanceFeet, MAX_CLOCK_DISTANCE_METERS } from '../../utils/geolocation';

interface TimeLog {
  id: string;
  clock_in_time: string;
  clock_out_time: string | null;
  status: string;
  hours_worked?: number;
}

const TimeClock: React.FC = () => {
  const { userProfile } = useAuth();
  const [currentLog, setCurrentLog] = useState<TimeLog | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [distance, setDistance] = useState<number | null>(null);
  const [checkingLocation, setCheckingLocation] = useState(false);

  useEffect(() => {
    fetchCurrentLog();
  }, [userProfile]);

  const fetchCurrentLog = async () => {
    if (!userProfile?.id) return;

    try {
      // Get staff record
      const { data: staffData } = await supabase
        .from('team')
        .select('id')
        .eq('user_id', userProfile.id)
        .single();

      if (!staffData) return;

      // Check for active clock-in
      const { data, error } = await supabase
        .from('crew_time_logs')
        .select('*')
        .eq('staff_id', staffData.id)
        .eq('status', 'clocked_in')
        .order('clock_in_time', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching current log:', error);
        return;
      }

      setCurrentLog(data);
    } catch (err) {
      console.error('Error in fetchCurrentLog:', err);
    }
  };

  const checkLocation = async (): Promise<boolean> => {
    setCheckingLocation(true);
    setError(null);
    setDistance(null);

    try {
      const result = await isWithinFacilityRange();
      setDistance(result.distance);

      if (!result.withinRange) {
        setError(
          `You are too far from the facility (${formatDistanceFeet(result.distance)} away). ` +
          `Please move within ${formatDistanceFeet(MAX_CLOCK_DISTANCE_METERS)}.`
        );
        return false;
      }

      return true;
    } catch (err: any) {
      if (err.code === 1) {
        setError('Location permission denied. Please enable location access in your browser settings.');
      } else if (err.code === 2) {
        setError('Location unavailable. Please check your GPS/network connection.');
      } else if (err.code === 3) {
        setError('Location request timed out. Please try again.');
      } else {
        setError('Unable to get your location. ' + (err.message || 'Unknown error'));
      }
      return false;
    } finally {
      setCheckingLocation(false);
    }
  };

  const handleClockIn = async () => {
    if (!userProfile?.id) return;

    setIsLoading(true);
    setError(null);

    try {
      // Check location first
      const locationOk = await checkLocation();
      if (!locationOk) {
        setIsLoading(false);
        return;
      }

      // Get current position for storage
      const result = await isWithinFacilityRange();

      // Get staff record
      const { data: staffData, error: staffError } = await supabase
        .from('team')
        .select('id')
        .eq('user_id', userProfile.id)
        .single();

      if (staffError || !staffData) {
        setError('Unable to find your staff record');
        setIsLoading(false);
        return;
      }

      // Create clock-in record
      const { data, error: insertError } = await supabase
        .from('crew_time_logs')
        .insert({
          staff_id: staffData.id,
          clock_in_time: new Date().toISOString(),
          clock_in_latitude: result.userLocation.latitude,
          clock_in_longitude: result.userLocation.longitude,
          status: 'clocked_in',
        })
        .select()
        .single();

      if (insertError) {
        setError('Failed to clock in: ' + insertError.message);
        setIsLoading(false);
        return;
      }

      setCurrentLog(data);
      setError(null);
    } catch (err: any) {
      setError('An error occurred: ' + (err.message || 'Unknown error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClockOut = async () => {
    if (!currentLog) return;

    setIsLoading(true);
    setError(null);

    try {
      // Check location first
      const locationOk = await checkLocation();
      if (!locationOk) {
        setIsLoading(false);
        return;
      }

      // Get current position for storage
      const result = await isWithinFacilityRange();

      // Update clock-out record
      const { error: updateError } = await supabase
        .from('crew_time_logs')
        .update({
          clock_out_time: new Date().toISOString(),
          clock_out_latitude: result.userLocation.latitude,
          clock_out_longitude: result.userLocation.longitude,
          status: 'clocked_out',
        })
        .eq('id', currentLog.id);

      if (updateError) {
        setError('Failed to clock out: ' + updateError.message);
        setIsLoading(false);
        return;
      }

      setCurrentLog(null);
      setError(null);
    } catch (err: any) {
      setError('An error occurred: ' + (err.message || 'Unknown error'));
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/Chicago',
    });
  };

  const calculateHoursWorked = () => {
    if (!currentLog) return '0:00';
    const start = new Date(currentLog.clock_in_time).getTime();
    const end = Date.now();
    const diff = end - start;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-xl font-semibold text-[#3A5E63] mb-4">Time Clock</h2>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          {error}
        </div>
      )}

      {distance !== null && distance <= MAX_CLOCK_DISTANCE_METERS && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md text-green-700 text-sm">
          ✓ You are within range ({formatDistanceFeet(distance)} from facility)
        </div>
      )}

      {currentLog ? (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-green-800">Currently Clocked In</span>
              <span className="inline-block w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
            </div>
            <p className="text-2xl font-semibold text-green-900">
              {formatTime(currentLog.clock_in_time)}
            </p>
            <p className="text-sm text-green-700 mt-2">
              Time worked: <span className="font-medium">{calculateHoursWorked()}</span>
            </p>
          </div>

          <button
            onClick={handleClockOut}
            disabled={isLoading || checkingLocation}
            className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
              isLoading || checkingLocation
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-red-600 hover:bg-red-700 text-white'
            }`}
          >
            {checkingLocation ? 'Checking Location...' : isLoading ? 'Clocking Out...' : 'Clock Out'}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
            <p className="text-gray-600">You are not clocked in</p>
          </div>

          <button
            onClick={handleClockIn}
            disabled={isLoading || checkingLocation}
            className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
              isLoading || checkingLocation
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-[#3A5E63] hover:bg-[#2d4a4e] text-white'
            }`}
          >
            {checkingLocation ? 'Checking Location...' : isLoading ? 'Clocking In...' : 'Clock In'}
          </button>
        </div>
      )}

      <div className="mt-4 text-xs text-gray-500 text-center">
        You must be within {formatDistanceFeet(MAX_CLOCK_DISTANCE_METERS)} of the facility to clock in/out
      </div>
    </div>
  );
};

export default TimeClock;



